import { TableInnerProps } from '../../models/table-models';
import { useTranslation } from 'react-i18next';
import {
    CompletedHandout,
    CompletedHandoutDistributorGroup,
    CompletedHandoutGroup,
    WarehouseReceiptProps
} from '../../models/warehouse-models';
import { completedHandoutTableHeaders } from '../../utils/Enums/TableHeaders';
import { PaginationComponent } from '../../components/Pagination';
import { NoDataComponent } from '../../components/NoDataComponent';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CustomButton } from '../../components/CustomButton';
import { Button, Collapse, Form, Modal, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { getWarehouseReceipt } from '../../utils/warehouses-hooks';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../../components/CustomToastContent';
import { toastOptionsTopRight } from '../../utils/Enums/ToastOptions';
import { checkered } from '../../styles/selectStyles';
import { Controller, useForm } from 'react-hook-form';
import CommonInputText from '../../components/CommonInputText';
import { DatePickerWrapper } from '../../components/DatePickerWrapper';
import dateFormat from 'dateformat';
import { date } from '../../utils/Enums/Common';

const fixedTableStyle: React.CSSProperties = {
    tableLayout: 'fixed',
    width: '100%',
};

const wrappingCellStyle: React.CSSProperties = {
    overflowWrap: 'anywhere',
    whiteSpace: 'normal',
};

const headerWithoutTopBorderStyle: React.CSSProperties = {
    borderTop: 'none',
};

const innerItemsTableContainerStyle: React.CSSProperties = {
    overflowX: 'auto',
    maxWidth: '100%',
};

const innerItemsTableStyle: React.CSSProperties = {
    tableLayout: 'auto',
    minWidth: '960px',
    width: '100%',
};

const innerItemsColumnWidths = ['220px', '150px', '90px', '150px', '190px', '90px', '110px', '100px'];

type CompletedHandoutDistributorInfo = Pick<
    CompletedHandoutDistributorGroup,
    'distributorName' | 'receivingUserId' | 'receivingUsername'
>;

type CompletedHandoutDateGroup = {
    dateKey: string;
    dateLabel: string;
    totalCount: number;
    items: CompletedHandout[];
    distributor: CompletedHandoutDistributorInfo;
};

type CompletedHandoutUserGroup = CompletedHandoutDistributorInfo & {
    userKey: string;
    totalCount: number;
    items: CompletedHandout[];
    dates: CompletedHandoutDateGroup[];
};

export const CompletedHandoutsTable = ({
    getEntities,
    pagination,
}: TableInnerProps<CompletedHandoutGroup>) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { t: tableT } = useTranslation('translation', { keyPrefix: 'table' });
    const [selectedItems, setSelectedItems] = useState<CompletedHandout[]>([]);
    const [props, setProps] = useState<WarehouseReceiptProps>({} as WarehouseReceiptProps);
    const [showModal, setShowModal] = useState(false);
    const [expandedDistributorKey, setExpandedDistributorKey] = useState<string>();
    const [expandedDateKeys, setExpandedDateKeys] = useState<string[]>([]);
    const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
    const { items, pager } = getEntities();

    const toggleDistributor = (distributorKey: string) => {
        setExpandedDistributorKey((currentKey) => (currentKey === distributorKey ? undefined : distributorKey));
        setExpandedDateKeys([]);
    };

    const getItemWithDistributor = useCallback((
        item: CompletedHandout,
        distributor: CompletedHandoutDistributorInfo,
    ): CompletedHandout => ({
        ...item,
        distributorName: distributor.distributorName,
        receivingUserId: distributor.receivingUserId,
        receivingUsername: distributor.receivingUsername,
    }), []);

    const getCompletedHandoutDateKey = (value?: string | Date) => {
        if (!value) return 'missing-handout-date';

        return dateFormat(new Date(value), 'yyyy-mm-dd');
    };

    const formatCompletedHandoutDate = (value?: string | Date) => {
        if (!value) return '-';

        return dateFormat(new Date(value), date);
    };

    const completedHandoutUserGroups = useMemo(() => Object.values(
        items.reduce<Record<string, Omit<CompletedHandoutUserGroup, 'dates'> & {
            dates: Record<string, CompletedHandoutDateGroup>;
        }>>((groups, group) => {
            group.distributors.forEach((distributor) => {
                const userKey = `${distributor.receivingUserId ?? distributor.receivingUsername ?? distributor.distributorName}`;

                if (!groups[userKey]) {
                    groups[userKey] = {
                        userKey,
                        distributorName: distributor.distributorName,
                        receivingUserId: distributor.receivingUserId,
                        receivingUsername: distributor.receivingUsername,
                        totalCount: 0,
                        items: [],
                        dates: {},
                    };
                }

                distributor.items.forEach((item) => {
                    const handoutDate = item.handoutDate ?? group.date;
                    const dateKey = getCompletedHandoutDateKey(handoutDate);
                    const dateLabel = formatCompletedHandoutDate(handoutDate);
                    const enrichedItem = getItemWithDistributor(
                        {
                            ...item,
                            handoutDate: item.handoutDate ?? group.date as unknown as Date,
                        },
                        distributor
                    );
                    const itemCount = item.itemCount ?? 0;

                    groups[userKey].totalCount += itemCount;
                    groups[userKey].items.push(enrichedItem);

                    if (!groups[userKey].dates[dateKey]) {
                        groups[userKey].dates[dateKey] = {
                            dateKey,
                            dateLabel,
                            totalCount: 0,
                            items: [],
                            distributor,
                        };
                    }

                    groups[userKey].dates[dateKey].totalCount += itemCount;
                    groups[userKey].dates[dateKey].items.push(enrichedItem);
                });
            });

            return groups;
        }, {})
    ).map((group) => ({
        ...group,
        dates: Object.values(group.dates),
    })), [getItemWithDistributor, items]);

    const toggleDate = (dateKey: string) => {
        setExpandedDateKeys((dateKeys) =>
            dateKeys.includes(dateKey)
                ? dateKeys.filter((key) => key !== dateKey)
                : [...dateKeys, dateKey],
        );
    };

    const getSelectableDateItemIds = (dateGroup: CompletedHandoutDateGroup) =>
        dateGroup.items
            .map((item) => item.id)
            .filter((itemId): itemId is number => itemId != null);

    const isDateSelected = useCallback((dateGroup: CompletedHandoutDateGroup) => {
        const dateItemIds = getSelectableDateItemIds(dateGroup);

        return dateItemIds.length > 0 && dateItemIds.every((itemId) => selectedItemIds.includes(itemId));
    }, [selectedItemIds]);

    const toggleSelectedDate = (dateGroup: CompletedHandoutDateGroup) => {
        const dateItemIds = getSelectableDateItemIds(dateGroup);

        if (dateItemIds.length === 0) {
            return;
        }

        setSelectedItemIds((ids) => {
            const isSelected = dateItemIds.every((itemId) => ids.includes(itemId));

            if (isSelected) {
                return ids.filter((id) => !dateItemIds.includes(id));
            }

            return Array.from(new Set([...ids, ...dateItemIds]));
        });
    };

    const hasAwaitingItems = (handoutItems: CompletedHandout[]) =>
        handoutItems.some((item) => item.state == null);

    const isSelectedItem = useCallback((item: CompletedHandout) => {
        return item.id != null && selectedItemIds.includes(item.id);
    }, [selectedItemIds]);

    useEffect(() => {
        const selectedHandouts = completedHandoutUserGroups
            .flatMap((group) => group.items)
            .filter(isSelectedItem);

        setSelectedItems(selectedHandouts);
    }, [completedHandoutUserGroups, isSelectedItem]);

    useEffect(() => {
        if (selectedItems.length > 0) {
            const item = selectedItems.at(0);
            if (item)
                setProps((prev: WarehouseReceiptProps) => ({
                    ...prev,
                    items: selectedItems.map((selectedItem) => ({
                        itemName: selectedItem.name,
                        amount: selectedItem.itemCount,
                        currency: '€',
                        price: selectedItem.price,
                    })),
                    taskId: item.taskId,
                    userId: item.receivingUserId,
                    creator: item.warehouseUsername,
                    date: dateFormat(new Date(), date),
                }));
        }
    }, [selectedItems]);
    const { register, control, handleSubmit, formState } = useForm<WarehouseReceiptProps>({
        defaultValues: props,
        mode: 'all',
    });
    const printWarehouseReceipt = async (formValue: WarehouseReceiptProps) => {
        const warehouseProps = { ...props, ...formValue };
        const pdfFile = await getWarehouseReceipt(warehouseProps);
        if (pdfFile) {
            const pdf = URL.createObjectURL(pdfFile);
            window.open(pdf, '_blank');
        } else {
            toast.error(
                <CustomToastContent content={[`Could not print warehouse receipt`]} whiteText />,
                toastOptionsTopRight
            );
        }
    };

    return (
        <>
            {pager.totalCount > 0 ? (
                <div className='w-100 d-flex flex-column justify-content-center align-items-center'>
                    <CustomButton
                        className='w-30 mb-3'
                        disabled={selectedItems.length === 0}
                        onClick={() => setShowModal((val) => !val)}
                        style={{ visibility: selectedItems.length > 0 ? 'visible' : 'hidden' }}
                        title={t('print') + ' ' + t('warehouseReceipt')}
                    />
                    <Table responsive className='w-100' style={fixedTableStyle}>
                        <colgroup>
                            <col style={{ width: '70%' }} />
                            <col style={{ width: '30%' }} />
                        </colgroup>
                        <thead className='background-dark color-main' style={headerWithoutTopBorderStyle}>
                            <tr>
                                <th className='single-line-text table-header text-center fw-bold' style={headerWithoutTopBorderStyle}>
                                    {tableT('distributor')}
                                </th>
                                <th className='single-line-text table-header text-center fw-bold' style={headerWithoutTopBorderStyle}>
                                    {tableT('totalCount')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className='accordionTableRow cursor-pointer'>
                            {completedHandoutUserGroups.map((group, groupIndex) => {
                                const distributorKey = group.userKey;
                                const groupHasAwaitingItems = hasAwaitingItems(group.items);

                                return (
                                    <React.Fragment key={distributorKey}>
                                        <tr
                                            className={`hovered-row ${checkered(groupIndex)}`}
                                            onClick={() => toggleDistributor(distributorKey)}
                                        >
                                            <td style={wrappingCellStyle}>
                                                <div className='d-flex align-items-center justify-content-start gap-2'>
                                                    <span>{group.distributorName ?? group.receivingUsername}</span>
                                                    {groupHasAwaitingItems && (
                                                        <FontAwesomeIcon
                                                            className='text-warning'
                                                            icon={faExclamationTriangle}
                                                            title={t('completedHandoutsAwaitingItemsTooltip')}
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                            <td>{group.totalCount}</td>
                                        </tr>
                                        <Collapse in={expandedDistributorKey === distributorKey}>
                                            <tr>
                                                <td colSpan={2} className='p-2'>
                                                    <Table responsive size='sm' className='mb-0' style={fixedTableStyle}>
                                                        <colgroup>
                                                            <col style={{ width: '60%' }} />
                                                            <col style={{ width: '20%' }} />
                                                            <col style={{ width: '20%' }} />
                                                        </colgroup>
                                                        <thead className='background-dark color-main' style={headerWithoutTopBorderStyle}>
                                                            <tr>
                                                                <th className='single-line-text table-header text-center fw-bold p-2' style={headerWithoutTopBorderStyle}>
                                                                    {tableT('date')}
                                                                </th>
                                                                <th className='single-line-text table-header text-center fw-bold p-2' style={headerWithoutTopBorderStyle}>
                                                                    {tableT('totalCount')}
                                                                </th>
                                                                <th className='single-line-text table-header text-center fw-bold p-2' style={headerWithoutTopBorderStyle}>
                                                                    {tableT('actions')}
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {group.dates.map((dateGroup, dateIndex) => {
                                                                const dateRowKey = `${distributorKey}-${dateGroup.dateKey}`;
                                                                const dateHasAwaitingItems = hasAwaitingItems(dateGroup.items);

                                                                return (
                                                                    <React.Fragment key={dateRowKey}>
                                                                        <tr
                                                                            className={`hovered-row ${checkered(dateIndex)}`}
                                                                            onClick={() => toggleDate(dateRowKey)}
                                                                        >
                                                                            <td style={wrappingCellStyle}>
                                                                                <div className='d-flex align-items-center justify-content-center gap-2'>
                                                                                    <span>{dateGroup.dateLabel}</span>
                                                                                    {dateHasAwaitingItems && (
                                                                                        <FontAwesomeIcon
                                                                                            className='text-warning'
                                                                                            icon={faExclamationTriangle}
                                                                                            title={t('completedHandoutsAwaitingItemsTooltip')}
                                                                                        />
                                                                                    )}
                                                                                </div>
                                                                            </td>
                                                                            <td>{dateGroup.totalCount}</td>
                                                                            <td
                                                                                className='text-center'
                                                                                onClick={(event) => event.stopPropagation()}
                                                                            >
                                                                                <Form.Check
                                                                                    checked={isDateSelected(dateGroup)}
                                                                                    disabled={getSelectableDateItemIds(dateGroup).length === 0}
                                                                                    onChange={() => toggleSelectedDate(dateGroup)}
                                                                                />
                                                                            </td>
                                                                        </tr>
                                                                        <Collapse in={expandedDateKeys.includes(dateRowKey)}>
                                                                            <tr>
                                                                                <td colSpan={3} className='p-2'>
                                                                                    <div style={innerItemsTableContainerStyle}>
                                                                                        <Table size='sm' className='mb-0' style={innerItemsTableStyle}>
                                                                                            <colgroup>
                                                                                                {innerItemsColumnWidths.map((width, index) => (
                                                                                                    <col key={index} style={{ width }} />
                                                                                                ))}
                                                                                            </colgroup>
                                                                                            <thead className='background-dark color-main' style={headerWithoutTopBorderStyle}>
                                                                                                <tr>
                                                                                                    {completedHandoutTableHeaders.map((header) => (
                                                                                                        <th
                                                                                                            key={header}
                                                                                                            className='single-line-text table-header text-center fw-bold p-2'
                                                                                                            style={headerWithoutTopBorderStyle}
                                                                                                        >
                                                                                                            {tableT(header)}
                                                                                                        </th>
                                                                                                    ))}
                                                                                                </tr>
                                                                                            </thead>
                                                                                            <tbody>
                                                                                                {dateGroup.items.map((item, itemIndex) => {
                                                                                                    return (
                                                                                                        <tr key={item.id ?? itemIndex} className={checkered(itemIndex)}>
                                                                                                            <td className='p-3' style={wrappingCellStyle}>{item.name}</td>
                                                                                                            <td className='p-3' style={wrappingCellStyle}>{item.batchCode}</td>
                                                                                                            <td className='p-3'>{item.itemCount}</td>
                                                                                                            <td className='p-3' style={wrappingCellStyle}>{item.receivingUsername}</td>
                                                                                                            <td className='p-3' style={wrappingCellStyle}>{item.distributorName}</td>
                                                                                                            <td className='p-3'>{item.price}</td>
                                                                                                            <td className='p-3'>
                                                                                                                {item.handoutDate ? dateFormat(new Date(item.handoutDate), date) : ''}
                                                                                                            </td>
                                                                                                            <td className='p-3'>
                                                                                                                {item.state == null ? t('Awaiting') : item.state ? t('Accepted') : t('Declined')}
                                                                                                            </td>
                                                                                                        </tr>
                                                                                                    );
                                                                                                })}
                                                                                            </tbody>
                                                                                        </Table>
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                        </Collapse>
                                                                    </React.Fragment>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </Table>
                                                </td>
                                            </tr>
                                        </Collapse>
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </Table>
                    <PaginationComponent
                        recordsCount={pager.totalCount}
                        pagination={{ ...pagination, pageSize: pagination.pageSize }}
                    />
                </div>
            ) : (
                <NoDataComponent noDataText={t('handouts')} />
            )}
            <Modal show={showModal} onHide={() => setShowModal(false)} size='lg' centered>
                <Modal.Header closeButton>
                    <Modal.Title id='contained-modal-title-vcenter'>{t('warehouseReceipt')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form
                        id='form'
                        className='d-flex flex-column align-items-center p-4  w-100'
                        noValidate
                        onSubmit={handleSubmit(printWarehouseReceipt)}
                    >
                        <Form.Group className=' d-flex flex-column mb-3 align-items-start w-100 '>
                            <CommonInputText
                                label={t('documentId')}
                                labelTextColor='text-black'
                                type='number'
                                fieldError={formState.errors.id}
                                hookRegister={register('id', {
                                    required: {
                                        value: true,
                                        message: t('id'),
                                    },
                                })}
                            />
                        </Form.Group>
                        <Form.Group className=' d-flex flex-column mb-3 align-items-start w-100 '>
                            <Form.Label>{t('date')}</Form.Label>
                            <Controller
                                defaultValue={new Date().toDateString()}
                                control={control}
                                name='date'
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <DatePickerWrapper
                                        dateMask={'dd.MM.yyyy'}
                                        selected={field.value}
                                        onChange={field.onChange}
                                    />
                                )}
                            />
                        </Form.Group>
                        <Form.Group className=' d-flex flex-column mb-3 align-items-start w-100 '>
                            <CommonInputText
                                label={t('town')}
                                labelTextColor='text-black'
                                type='text'
                                fieldError={formState.errors.town}
                                hookRegister={register('town', {
                                    required: {
                                        value: true,
                                        message: t('town'),
                                    },
                                })}
                            />
                        </Form.Group>
                        <h4 className='mb-3'>{t('handedOverItems')}</h4>
                        {selectedItems.map((handout, index) => (
                            <div
                                key={index}
                                className={`border d-flex p-2 justify-content-between w-50 ${checkered(index)}`}
                            >
                                <div>{handout.name}:</div>
                                <div className='align-self-end'>{handout.itemCount}</div>
                            </div>
                        ))}

                        <Button className='mt-3' type='submit'>
                            {t('print')}
                        </Button>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => setShowModal(false)}>{t('close')}</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};
